import React, { useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { ArrowDownTrayIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';

interface ExcelDownloadUploadProps {
  roadmapId: string;
  isTenantRoadmap?: boolean;
  onImportSuccess?: (roadmapId: string) => void;
}

export const ExcelDownloadUpload: React.FC<ExcelDownloadUploadProps> = ({
  roadmapId,
  isTenantRoadmap = false,
  onImportSuccess
}) => {
  useLanguage();
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showFileInput, setShowFileInput] = useState(false);

  // =====================================================
  // DOWNLOAD FUNCTIONALITY
  // =====================================================

  const handleDownload = async () => {
    try {
      setDownloading(true);
      setError(null);
      
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        setError('No authentication token found. Please log in again.');
        return;
      }
      
      const response = await fetch(
        `/api/v1/marketplace/roadmaps/${roadmapId}/export-excel?tenant=${isTenantRoadmap}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Download failed:', response.status, errorText);
        throw new Error(`Failed to download Excel file: ${response.status} - ${errorText}`);
      }

      // Get filename from response headers
      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition 
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
        : `roadmap-${roadmapId}.xlsx`;

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (err: any) {
      setError(err.message || 'Failed to download Excel file');
    } finally {
      setDownloading(false);
    }
  };

  // =====================================================
  // UPLOAD FUNCTIONALITY
  // =====================================================

  const handleImportClick = () => {
    setShowFileInput(true);
    // Trigger file input click
    document.getElementById('excel-upload')?.click();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handlePreview(file);
    }
  };


  const handlePreview = async (file: File) => {
    try {
      setUploading(true);
      setError(null);

      
      const formData = new FormData();
      formData.append('excelFile', file);
      formData.append('roadmapId', roadmapId);

      const previewRoute = isTenantRoadmap 
        ? `/api/v1/marketplace/roadmaps/${roadmapId}/preview-excel?tenant=true`  // Tenant preview route
        : `/api/v1/marketplace/roadmaps/preview-excel`;  // Template preview route
      
            
      const response = await fetch(previewRoute, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      const result = await response.json();

      if (!response.ok) {
        // Extract the specific error message from the backend response
        const errorMessage = result.error || result.message || 'Failed to preview Excel file';
        throw new Error(errorMessage);
      }

      // Store the file and show preview
      setSelectedFile(file);
      setPreviewData(result.data);
      setShowPreview(true);
      
    } catch (err: any) {
      setError(err.message || 'Failed to preview Excel file');
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = async (file: File) => {
    try {
      setUploading(true);
      setError(null);

      const formData = new FormData();
      formData.append('excelFile', file);
      formData.append('roadmapId', roadmapId);

      const importRoute = isTenantRoadmap 
        ? `/api/v1/marketplace/roadmaps/${roadmapId}/import-excel?tenant=true`  // Tenant import route
        : `/api/v1/marketplace/roadmaps/import-template-excel`;  // Template import route
      
      const response = await fetch(importRoute, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: formData
      });

      const result = await response.json();

      if (!response.ok) {
        // Extract the specific error message from the backend response
        const errorMessage = result.error || result.message || 'Failed to import Excel file';
        throw new Error(errorMessage);
      }

      if (onImportSuccess) {
        onImportSuccess(result.data.roadmapId);
      }
      
    } catch (err: any) {
      setError(err.message || 'Failed to import Excel file');
    } finally {
      setUploading(false);
    }
  };

  // =====================================================
  // CONFIRMATION FUNCTIONALITY
  // =====================================================

  const handleConfirmImport = async () => {
    if (!previewData || !selectedFile) return;
    
    // Upload the file for actual import
    await handleUpload(selectedFile);
    setShowPreview(false);
    setPreviewData(null);
    setSelectedFile(null);
  };

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <div className="space-y-4">
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {/* Consolidated Excel Export/Import Section */}
      <div className="bg-[var(--color-surface-bg)] border border-[var(--color-border)] rounded-lg p-6">
        <h3 className="text-lg font-semibold text-[var(--color-surface-text)] mb-4">
          📊 Excel Export/Import
        </h3>
        <p className="text-sm text-[var(--color-secondary-text)] mb-6">
          Export this roadmap to Excel for editing, then import your changes back to update the roadmap.
        </p>
        
        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Export Button */}
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowDownTrayIcon className="w-5 h-5" />
            {downloading ? 'Exporting...' : 'Export'}
          </button>

          {/* Import Button */}
          <button
            onClick={handleImportClick}
            disabled={uploading}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowUpTrayIcon className="w-5 h-5" />
            {uploading ? 'Processing...' : 'Import'}
          </button>
        </div>

        {/* Hidden File Input */}
        <input
          id="excel-upload"
          type="file"
          accept=".xlsx,.xls"
          onChange={handleFileSelect}
          disabled={uploading}
          className="hidden"
        />

        {/* File Input Display (shown after Import button is clicked) */}
        {showFileInput && (
          <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <label className="block text-sm font-medium text-[var(--color-surface-text)] mb-2">
              Selected File
            </label>
            <p className="text-sm text-[var(--color-secondary-text)]">
              {selectedFile ? selectedFile.name : 'No file selected'}
            </p>
            <p className="text-xs text-[var(--color-secondary-text)] mt-2">
              The file will be automatically analyzed and changes will be shown for confirmation before importing.
            </p>
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {showPreview && previewData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[var(--color-surface-bg)] rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-[var(--color-border)]">
              <h2 className="text-xl font-semibold text-[var(--color-surface-text)]">
                Excel Import Changes
              </h2>
              <button
                onClick={() => setShowPreview(false)}
                className="text-[var(--color-secondary-text)] hover:text-[var(--color-primary-text)] transition-colors"
              >
                ✕
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-blue-50 p-4 rounded-md">
                <h3 className="font-semibold text-blue-900 mb-2">Import Confirmation</h3>
                <p className="text-sm text-blue-800">
                  This will import the Excel file and update your roadmap.
                </p>
              </div>

              {/* Show changes if available */}
              {previewData?.changes && previewData.changes.length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Changes to be made:</h4>
                  <ul className="space-y-2">
                    {previewData.changes.map((change: string, index: number) => (
                      <li key={index} className="text-sm text-gray-700 flex items-start">
                        <span className="text-green-600 mr-2">•</span>
                        <span>{change}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                <p className="text-sm text-yellow-800">
                  ⚠️ <strong>Warning:</strong> This will update the existing roadmap. 
                  Any changes will be applied immediately. Make sure you have a backup if needed.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleConfirmImport}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Confirm Import
                </button>
                <button
                  onClick={() => setShowPreview(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 border border-gray-300 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExcelDownloadUpload;
